import { useQuery } from "@tanstack/react-query";
import {
  Navigate,
  Outlet,
  createBrowserRouter,
  useLocation,
} from "react-router-dom";
import { Loader2 } from "lucide-react";
import { ApiError, api, onAuthExpired } from "./lib/api";
import { queryClient } from "./lib/queryClient";
import { BrandProvider } from "./lib/brand";
import { useSessionUser } from "./lib/session";
import { ConsoleLayout } from "./components/ConsoleLayout";
import { WorkspaceLayout } from "./components/WorkspaceLayout";
import { Login } from "./routes/Login";
import { ChangePassword } from "./routes/ChangePassword";
import { Dashboard } from "./routes/Dashboard";
import { Brands } from "./routes/Brands";
import { BrandDetail } from "./routes/BrandDetail";
import { Manajers } from "./routes/Manajers";
import { Payments } from "./routes/Payments";
import { Settings } from "./routes/Settings";
import { DraftsList } from "./routes/DraftsList";
import { DraftDetail } from "./routes/DraftDetail";
import { SpecialCase } from "./routes/SpecialCase";
import { Articles } from "./routes/Articles";
import { ArticleDetail } from "./routes/ArticleDetail";
import { Custom } from "./routes/Custom";
import { Carousel } from "./routes/Carousel";
import { CarouselDetail } from "./routes/CarouselDetail";
import { Media } from "./routes/Media";
import { Materi } from "./routes/Materi";
import { Video } from "./routes/Video";
import { Connections } from "./routes/Connections";

/**
 * Router basename. In production the SPA is served under `/app` (single-origin),
 * so Vite sets BASE_URL to "/app/"; in dev it is "/". Strip the trailing slash.
 */
const BASENAME = import.meta.env.BASE_URL.replace(/\/$/, "") || "/";
const LOGIN_PATH = `${BASENAME === "/" ? "" : BASENAME}/login`;

/**
 * Global 401 handling: clear the cached session and bounce to /login (unless
 * already there). Registered once at module load.
 */
onAuthExpired(() => {
  queryClient.setQueryData(["me"], null);
  queryClient.removeQueries({ queryKey: ["me"] });
  if (window.location.pathname !== LOGIN_PATH) {
    window.location.assign(LOGIN_PATH);
  }
});

/**
 * Resolves the session via `/auth/me`. While loading shows a spinner; on auth
 * failure redirects to /login. On success, provides the multi-brand context to
 * every layout below.
 */
function ProtectedRoute() {
  const location = useLocation();
  const meQuery = useQuery({
    queryKey: ["me"],
    queryFn: ({ signal }) => api.me(signal),
    retry: false,
    staleTime: 60_000,
  });

  if (meQuery.isLoading) {
    return (
      <div className="flex min-h-full items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-teal" />
      </div>
    );
  }

  if (meQuery.isError || !meQuery.data) {
    const status =
      meQuery.error instanceof ApiError ? meQuery.error.status : undefined;
    void status;
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // An admin-reset user must set a new password before anything else. Force the
  // change-password screen, rendered WITHOUT BrandProvider — its `/accounts`
  // fetch is blocked by the backend until the password is changed.
  if (meQuery.data.mustChangePassword) {
    if (location.pathname !== "/change-password") {
      return <Navigate to="/change-password" replace />;
    }
    return <Outlet />;
  }

  return (
    <BrandProvider user={meQuery.data}>
      <Outlet />
    </BrandProvider>
  );
}

/** Index landing: admins → Console dashboard; others → their brand workspace. */
function HomeRedirect() {
  const user = useSessionUser();
  return (
    <Navigate to={user.role === "admin" ? "/dashboard" : "/drafts"} replace />
  );
}

export const router = createBrowserRouter(
  [
    {
      path: "/login",
      element: <Login />,
    },
    {
      element: <ProtectedRoute />,
      children: [
        { index: true, element: <HomeRedirect /> },

        // Forced (admin-reset) or voluntary password change — full-screen, no layout.
        { path: "change-password", element: <ChangePassword /> },

        // ── Admin Console (sidebar): management / master data ──
        {
          element: <ConsoleLayout />,
          children: [
            { path: "dashboard", element: <Dashboard /> },
            { path: "brands", element: <Brands /> },
            { path: "brands/:key", element: <BrandDetail /> },
            { path: "manajers", element: <Manajers /> },
            { path: "payments", element: <Payments /> },
            { path: "settings", element: <Settings /> },
          ],
        },

        // ── Brand Workspace (top nav): per-brand content (all roles) ──
        {
          element: <WorkspaceLayout />,
          children: [
            { path: "drafts", element: <DraftsList mode="reguler" /> },
            { path: "drafts/:id", element: <DraftDetail /> },
            { path: "special", element: <SpecialCase /> },
            { path: "articles", element: <Articles /> },
            { path: "articles/:id", element: <ArticleDetail /> },
            { path: "custom", element: <Custom /> },
            { path: "carousel", element: <Carousel /> },
            { path: "carousel/:id", element: <CarouselDetail /> },
            { path: "media", element: <Media /> },
            { path: "materials", element: <Materi /> },
            { path: "videos", element: <Video /> },
            { path: "connections", element: <Connections /> },
          ],
        },
      ],
    },
    {
      path: "*",
      element: <Outlet />,
      children: [{ path: "*", element: <Navigate to="/" replace /> }],
    },
  ],
  { basename: BASENAME },
);
